import { useCallback, useMemo } from 'react';
import { useSession } from 'src/contexts/SessionContext';
import { getCapability } from 'src/models/capability/utils';
import { DashboardLayoutObject } from 'src/models/dashboard/_types';
import { isGroupAdmin, isGroupMember } from 'src/models/group/utils';
import { FeatureFlag, PlatformTab } from 'src/models/platform/_types';
import { isHQPlatform } from 'src/models/platform/utils';
import {
  isAdmin,
  isHeadTrainer,
  isSubTrainer,
  isTrainer,
} from 'src/models/user/utils';
import { useHQPermissionCheck } from 'src/shared/HQ/useHQPermissionCheck';

import type { Appointment } from 'src/models/appointment/_types';
import type { Capability } from 'src/models/capability/_types';
import type { ExerciseDetail } from 'src/models/exercise/_types';
import type { GroupMembership } from 'src/models/group/_types';
import type { Group } from 'src/models/group/_types';
import type { Routine } from 'src/models/routine/_types';
import type { Session } from 'src/models/session/_types';
import type { RoleProps, Trainer } from 'src/models/trainer/_types';
import type { User } from 'src/models/user/_types';
import type { WorkoutPlan } from 'src/models/workout-plan/workout-plan';
import type { Identifier } from 'src/types/utilityTypes';

export interface Permissions {
  trainer: Trainer | null;
  sessionUser: User;
  permissionsLoading: boolean;
  permissionsError: Error | null;
  canViewTimeTracking: boolean;
  canEditNoteFields: boolean;
  canViewTasks: boolean;
  canViewOtherTimeClock: boolean;
  canEditLogEntries: boolean;
  canClockIn: (user: User) => boolean;
  canViewStaffUserMenu: boolean;
  canViewWebhooks: boolean;
  canViewApiKeys: boolean;
  canExportTimeClockReport: boolean;
  canViewOthersScheduling: boolean;
  canGlobalCheckIn: boolean;
  canViewMessages: boolean;
  canViewAppointmentMembers: (appointment: Appointment) => boolean;
  canEditAppointmentMembers: (appointment: Appointment) => boolean;
  canCheckInAppointment: (appointment: Appointment) => boolean;
  canViewTrainerTab: (tab: Partial<PlatformTab>) => boolean;
  canViewScheduleSubtab: (tab: PlatformTab) => boolean;
  canViewTrainerSidebarTabs: (tab: PlatformTab) => boolean;
  canViewClientTab: (tab: Partial<PlatformTab>) => boolean;
  canViewClientManagementTab: (tab: Partial<PlatformTab>) => boolean;
  canViewBillingInfo: boolean;
  canOnlyManageLocationClient: boolean;
  canManageClientsForTrainer: boolean;
  canViewAssessment: boolean;
  canNotEditYourAssessment: boolean;
  canViewGroup: boolean;
  canAssignWorkoutPlan: boolean;
  canViewResource: boolean;
  canViewVideo: boolean;
  canNotEditVideo: (subTrainerId: Identifier) => boolean;
  canCreateGroup: boolean;
  canEditGroup: boolean;
  canMangeGroupDiscussion: (group: Group) => boolean;
  canUploadGroupMedia: (
    group: Group,
    memberships?: GroupMembership[] | null
  ) => boolean;
  canScheduleGroupDiscussion: (group: Group) => boolean;
  canPostGroupDiscussion: (
    group: Group,
    memberships?: GroupMembership[] | null,
    isAdminOfGroup?: boolean
  ) => boolean;
  cannotEditExercise: (exercise: ExerciseDetail) => boolean;
  canImportExercise: boolean;
  canViewRoutine: boolean;
  canEditRoutine: boolean;
  cannotEditRoutine: (routine: Routine) => boolean;
  canViewWorkoutPlan: boolean;
  cannotEditWorkoutPlan: (plan: WorkoutPlan) => boolean;
  canCancelVisit: boolean;
  canEarlyCancelVisit: boolean;
  canViewDashboardObject: (object: DashboardLayoutObject) => boolean;
  canManageAppNavigation: boolean;
  canManagePages: boolean;
  canManageAll: boolean;
  canManageStripe: boolean;
  canManageTrainerDashboardNavigation: boolean;
  canManageTrainerAccountNavigation: boolean;
  canManageClientManagementNavigation: boolean;
  canManageClientDashboardNavigation: boolean;
  canManageClientAccountNavigation: boolean;
  canEditOthersSchedules: boolean;
  canEditSchedules: boolean;
  canManageServices: boolean;
  canConnectStripe: boolean;
  canConnectApps: boolean;
  canAddRevenueShare: boolean;
  canViewClient: boolean;
  canViewInventory: boolean;
  canViewOtherLocationInventory: boolean;
  canAdjustInventory: boolean;
  canAdjustOtherLocationInventory: boolean;
  canTransferInventory: boolean;
  canViewInventoryHistory: boolean;
  canExportInventoryHistory: boolean;
  canViewDashboardKpi: (kpiId: string) => boolean;
  canViewDashboardChart: (chartId: string) => boolean;
  canViewPos: boolean;
  canManagePos: boolean;
  currentUserCanManageAllMessages: boolean;
  currentUserCanManageLocationMessages: boolean;
  canViewMacroCalculatorSettings: boolean;
  canViewHQFranchiseFees: () => boolean;
  canEditHQFranchiseFees: () => boolean;
  canAccessFrontDeskAsStaff: boolean;
  canAccessFrontDeskAsAdmin: boolean;
  canManageLocations: boolean;
  canViewScheduling: boolean;
  canViewReport: (reportType: string) => boolean;
}

// TODO: There are a couple permission schemes so will need two enums
enum TrainerPermissions {
  ManageAll = 0,
  ManageOwn = 1,
  ManageNone = 2,
  ManageLocation = 3,
}

export const usePermissionCheck = (): Permissions => {
  const {
    capabilities,
    session,
    user: sessionUser,
    trainer,
    trainerError,
    trainerLoading,
  } = useSession();

  const hasTrainerPermission = useCallback(
    (permission: keyof RoleProps, value: number, exactMatch = false) => {
      return (
        isHeadTrainer(sessionUser) ||
        (isSubTrainer(sessionUser) &&
          trainer &&
          (exactMatch
            ? trainer[permission] == value
            : trainer[permission] <= value))
      );
    },
    [sessionUser, trainer]
  );

  const hasPermission = useCallback(
    (permission: keyof RoleProps, value: number) => {
      return trainer[permission] == value;
    },
    [trainer]
  );

  const hasNotEditPermission = useCallback(
    <T extends { id: Identifier; subTrainerId: Identifier | null }>(
      permission: keyof RoleProps,
      capabilityOnType: Capability['capabilityOnType'],
      model: T
    ) => {
      if (isHeadTrainer(sessionUser)) return false;
      const capability = getCapability(
        capabilities,
        model.id,
        capabilityOnType
      );
      return (
        (capability &&
          capability.permission !== TrainerPermissions.ManageAll) ||
        (!capability &&
          isSubTrainer(sessionUser) &&
          hasTrainerPermission(
            permission,
            TrainerPermissions.ManageNone,
            true
          ) &&
          model.subTrainerId !== sessionUser.id) ||
        (!capability &&
          isSubTrainer(sessionUser) &&
          hasTrainerPermission(
            permission,
            TrainerPermissions.ManageLocation,
            true
          ) &&
          !model.subTrainerId)
      );
    },
    [capabilities, hasTrainerPermission, sessionUser]
  );

  const isAdminTrainer = useCallback(() => {
    if (isHeadTrainer(sessionUser)) return true;
    if (!isSubTrainer(sessionUser)) return false;

    return (
      hasTrainerPermission('assessmentsRole', TrainerPermissions.ManageAll) &&
      hasTrainerPermission('schedulesRole', TrainerPermissions.ManageAll) &&
      hasTrainerPermission('resourcesRole', TrainerPermissions.ManageAll) &&
      hasTrainerPermission('videosRole', TrainerPermissions.ManageAll) &&
      hasTrainerPermission('routinesRole', TrainerPermissions.ManageAll) &&
      hasTrainerPermission('subtrainerRole', TrainerPermissions.ManageAll) &&
      hasTrainerPermission('clockRole', TrainerPermissions.ManageAll) &&
      hasTrainerPermission('connectedAppsRole', TrainerPermissions.ManageAll) &&
      hasTrainerPermission('workoutPlansRole', TrainerPermissions.ManageAll) &&
      hasTrainerPermission('exercisesRole', TrainerPermissions.ManageAll) &&
      hasTrainerPermission('clientsRole', TrainerPermissions.ManageAll) &&
      hasTrainerPermission('schedulingRole', TrainerPermissions.ManageAll) &&
      hasTrainerPermission('cancelVisitsRole', TrainerPermissions.ManageAll) &&
      hasTrainerPermission(
        'clientPackagesRole',
        TrainerPermissions.ManageAll
      ) &&
      hasTrainerPermission('groupsRole', TrainerPermissions.ManageAll) &&
      hasTrainerPermission('inventoryRole', TrainerPermissions.ManageAll)
    );
  }, [hasTrainerPermission, sessionUser]);

  const canManageAll = isAdminTrainer();

  // clock roles
  const canViewTimeTracking = hasFeatureFlag(session, FeatureFlag.TimeTracking);

  // clock roles
  const canViewTasks = hasTrainerPermission(
    'clockRole',
    TrainerPermissions.ManageAll
  );
  const canEditLogEntries = hasTrainerPermission(
    'clockRole',
    TrainerPermissions.ManageAll
  );
  // can view all time clocks
  const canViewOtherTimeClock = hasTrainerPermission('clockRole', 1);

  const canClockIn = useCallback(
    (user: User) => {
      if (isHeadTrainer(sessionUser)) return true;
      if (user && user.id === sessionUser.id)
        return trainer && trainer.clockRole <= 2;

      return (
        isSubTrainer(sessionUser) &&
        trainer &&
        trainer.clockRole <= TrainerPermissions.ManageAll
      );
    },
    [sessionUser, trainer]
  );

  const canEditNoteFields =
    isHeadTrainer(sessionUser) ||
    (isSubTrainer(sessionUser) &&
      hasTrainerPermission('clientsRole', TrainerPermissions.ManageAll));

  const canViewStaffUserMenu =
    isHeadTrainer(sessionUser) || isSubTrainer(sessionUser);

  const canViewWebhooks =
    isHeadTrainer(sessionUser) && hasFeatureFlag(session, FeatureFlag.Webhooks);
  const canViewApiKeys =
    isHeadTrainer(sessionUser) &&
    hasFeatureFlag(session, FeatureFlag.ApiAccess);
  const canExportTimeClockReport =
    isHeadTrainer(sessionUser) ||
    (trainer && trainer.reportsRole.includes('time_clock_entries'));

  // appointment permissions
  const canViewOthersScheduling =
    isHeadTrainer(sessionUser) ||
    (isSubTrainer(sessionUser) &&
      !hasTrainerPermission('schedulingRole', 3, true) &&
      !hasTrainerPermission('schedulingRole', 5, true));

  const canViewAppointmentMembers = useCallback(
    (appointment: Appointment) => {
      return (
        isSubTrainer(sessionUser) ||
        isHeadTrainer(sessionUser) ||
        appointment.service.showBooked
      );
    },
    [sessionUser]
  );

  const canEditAppointmentMembers = useCallback(
    (appointment: Appointment) => {
      return (
        hasTrainerPermission('schedulingRole', TrainerPermissions.ManageAll) ||
        hasTrainerPermission('schedulingRole', 2) ||
        appointment.trainerIds.includes(sessionUser.id)
      );
    },
    [sessionUser, hasTrainerPermission]
  );

  const canCheckInAppointment = useCallback(
    (appointment: Appointment) => {
      return (
        !session.disableClientCheckin ||
        !appointment.service.disableCancellations
      );
    },
    [session]
  );

  const canViewBillingInfo =
    !hasFeatureFlag(session, FeatureFlag.DisableBillingInfo) ||
    isHeadTrainer(sessionUser);

  const canGlobalCheckIn = !session?.disableClientCheckin;

  const canViewMessages = useMemo(() => {
    if (isHeadTrainer(sessionUser)) return true;
    if (isSubTrainer(sessionUser)) {
      return (
        hasTrainerPermission('messagesRole', TrainerPermissions.ManageOwn) ||
        hasTrainerPermission(
          'messagesRole',
          TrainerPermissions.ManageLocation,
          true
        )
      );
    }

    return !sessionUser.trainerMessagingDisabled;
  }, [sessionUser, hasTrainerPermission]);

  const canViewTrainerTab = useCallback(
    (tab: Partial<PlatformTab>) => {
      if (!isHeadTrainer(sessionUser) && !isSubTrainer(sessionUser)) {
        return false;
      }

      if (isHeadTrainer(sessionUser) && tab.id != 'Schedule') {
        return true;
      }

      switch (tab.id) {
        case 'Messages':
          return (
            hasTrainerPermission('messagesRole', 1) ||
            hasTrainerPermission('messagesRole', 3, true)
          );
        case 'Automations':
          return isAdminTrainer();
        case 'Clients':
          return (
            hasTrainerPermission('clientsRole', 1) ||
            hasTrainerPermission('clientsRole', 3, true)
          );
        case 'Groups':
          return hasTrainerPermission('groupsRole', 3);
        case 'Exercises':
          return hasTrainerPermission('exercisesRole', 3);
        case 'Routines':
          return hasTrainerPermission('routinesRole', 3);
        case 'Plans':
          return hasTrainerPermission('workoutPlansRole', 3);
        case 'Packages':
        case 'Services':
        case 'Schedule':
          return (
            session &&
            session.enableFbm &&
            (isHeadTrainer(sessionUser) ||
              hasTrainerPermission('schedulingRole', 2) ||
              hasTrainerPermission('schedulingRole', 4, true) ||
              hasTrainerPermission('schedulingRole', 5, true))
          );
        default:
          return true;
      }
    },
    [sessionUser, hasTrainerPermission, session, isAdminTrainer]
  );

  const canViewClientManagementTab = useCallback(
    (tab: Partial<PlatformTab>) => {
      // ensure session is loaded, user is trainer
      if (
        !session ||
        (!isHeadTrainer(sessionUser) && !isSubTrainer(sessionUser))
      ) {
        return false;
      }

      // head trainer can view all tabs
      if (isHeadTrainer(sessionUser)) {
        return true;
      }

      switch (tab.id) {
        default:
          return !(trainer?.disabledTabs || []).includes(tab.id || '');
      }
    },
    [session, sessionUser, trainer]
  );

  const canViewScheduleSubtab = useCallback(
    (tab: PlatformTab) => {
      if (isHeadTrainer(sessionUser)) {
        return true;
      }
      if (!isSubTrainer(sessionUser)) {
        return false;
      }
      switch (tab.id) {
        case 'calendar':
          return (
            session &&
            session.enableFbm &&
            (isHeadTrainer(sessionUser) ||
              hasTrainerPermission('schedulingRole', 2) ||
              hasTrainerPermission('schedulingRole', 4, true) ||
              hasTrainerPermission('schedulingRole', 5, true))
          );
        case 'visits':
        case 'members':
          return (
            hasTrainerPermission('schedulingRole', 2) ||
            hasTrainerPermission('schedulingRole', 5, true)
          );
        case 'services':
        case 'packages':
        case 'locations':
          return (
            hasTrainerPermission('schedulingRole', 1) ||
            hasTrainerPermission('schedulingRole', 5, true)
          );
        case 'schedules':
          return hasTrainerPermission('schedulesRole', 1);
        default:
          return true;
      }
    },
    [sessionUser, hasTrainerPermission, session]
  );

  const canManageTrainerDashboardNavigation =
    isTrainer(sessionUser) &&
    (!session?.disableNavigationTrainerDashboardTabs ||
      isHQPlatform(session));
  const canManageTrainerAccountNavigation =
    isTrainer(sessionUser) &&
    (!session?.disableNavigationTrainerAccountTabs ||
      isHQPlatform(session));
  const canManageClientManagementNavigation =
    isTrainer(sessionUser) &&
    (!session?.disableNavigationClientManagementTabs ||
      isHQPlatform(session));
  const canManageClientDashboardNavigation =
    isTrainer(sessionUser) &&
    (!session?.disableNavigationClientDashboardTabs ||
      isHQPlatform(session));
  const canManageClientAccountNavigation =
    isTrainer(sessionUser) &&
    (!session?.disableNavigationClientAccountTabs ||
      isHQPlatform(session));

  const canManageAppNavigation = sessionUser && isAdmin(sessionUser); // || isAdminTrainer();
  const canManagePages =
    sessionUser && isHeadTrainer(sessionUser) && isAdminTrainer();

  const canViewDashboardObject = useCallback(
    (object: DashboardLayoutObject) => {
      switch (object.originalId || object.id) {
        case 'last_transfer':
          return hasTrainerPermission('connectedAppsRole', 0);
        case 'next_transfer':
          return hasTrainerPermission('connectedAppsRole', 0);
        case 'balance_pending':
          return hasTrainerPermission('connectedAppsRole', 0);
        case 'balance_available':
          return hasTrainerPermission('connectedAppsRole', 0);
        case 'balance_available_instant':
          return hasTrainerPermission('connectedAppsRole', 0);
        case 'in_transit':
          return hasTrainerPermission('connectedAppsRole', 0);
        case 'active_subscriptions':
          return hasTrainerPermission('clientsRole', 0);
        case 'scheduled_subscriptions':
          return hasTrainerPermission('clientsRole', 0);
        case 'paused':
          return hasTrainerPermission('clientsRole', 0);
        case 'pending_cancel':
          return hasTrainerPermission('clientsRole', 0);
        case 'total_packages':
          return hasTrainerPermission('clientsRole', 0);
        case 'total_package_users':
          return hasTrainerPermission('clientsRole', 0);
        case 'lifecycle_stage_count':
          return hasTrainerPermission('clientsRole', 0);
        case 'new_accounts':
          return true;
        case 'failed_payments':
          return true;
        case 'no_logged_workouts':
          return true;
        case 'expiring_package_accounts':
          return true;
        case 'expiring_card_accounts':
          return true;
        case 'completed_assessment':
          return hasTrainerPermission('clientsRole', 0);
        case 'birthday_soon':
          return true;
        case 'no_recent_visits':
          return true;
        case 'calendar_plan_ending':
          return true;
        case 'calendar_ending':
          return true;
        case 'new_uploads':
          return true;
        case 'new_messages':
          return true;
        case 'no_package_accounts':
          return true;
        case 'active_packages':
          return hasTrainerPermission('clientsRole', 0);
        case 'active_package_users':
          return hasTrainerPermission('clientsRole', 0);
        case 'package_sales':
          return hasTrainerPermission('clientsRole', 0);
        case 'gross_sales':
          return hasTrainerPermission('clientsRole', 0);
        case 'product_sales':
          return hasTrainerPermission('clientsRole', 0);
        case 'succeeded_charges':
          return hasTrainerPermission('clientsRole', 0);
        case 'group_sales':
          return hasTrainerPermission('clientsRole', 0);
        case 'plan_sales':
          return hasTrainerPermission('clientsRole', 0);
        case 'refunded_charges':
          return hasTrainerPermission('clientsRole', 0);
        case 'failed_invoice_charges':
          return hasTrainerPermission('clientsRole', 0);
        case 'active_clients':
          return hasTrainerPermission('clientsRole', 0);
        case 'new_clients':
          return hasTrainerPermission('clientsRole', 0);
        case 'lead_assessment_completed':
          return hasTrainerPermission('clientsRole', 0);
        case 'clients_on_active_package_subscription':
          return hasTrainerPermission('clientsRole', 0);
        case 'active_package_subscription':
          return hasTrainerPermission('clientsRole', 0);
        case 'clients_on_active_one_time_package':
          return hasTrainerPermission('clientsRole', 0);
        case 'clients_on_active_one_time_free_package':
          return hasTrainerPermission('clientsRole', 0);
        case 'booked_visits':
          return hasTrainerPermission('clientsRole', 0);
        case 'scheduled_visits':
          return hasTrainerPermission('clientsRole', 0);
        case 'complete_visits':
          return hasTrainerPermission('clientsRole', 0);
        case 'cancel_visits':
          return hasTrainerPermission('clientsRole', 0);
        case 'late_cancel_visits':
          return hasTrainerPermission('clientsRole', 0);
        case 'noshow_visits':
          return hasTrainerPermission('clientsRole', 0);
        case 'subscription_sales':
          return hasTrainerPermission('clientsRole', 0);
        case 'projected_sales':
          return hasTrainerPermission('clientsRole', 0);
        case 'total_workouts':
          return hasTrainerPermission('clientsRole', 0);
        case 'total_plan_followers':
          return hasTrainerPermission('clientsRole', 0);
        case 'new_subscriptions':
          return hasTrainerPermission('clientsRole', 0);
        case 'cancelled_subscriptions':
          return hasTrainerPermission('clientsRole', 0);
        default:
          return true;
      }
    },
    [hasTrainerPermission]
  );

  const canViewClientTab = useCallback(
    (tab: Partial<PlatformTab>) => {
      switch (tab.id) {
        case 'Messages':
          return isTrainer(sessionUser) || canViewMessages;
        default:
          return true;
      }
    },
    [sessionUser, canViewMessages]
  );

  const canViewTrainerSidebarTabs = useCallback(
    (tab: PlatformTab) => {
      if (!isHeadTrainer(sessionUser) && !isSubTrainer(sessionUser)) {
        return false;
      }

      if (isHeadTrainer(sessionUser) && tab.id != 'Email Templates') {
        return true;
      }

      switch (tab.id) {
        case 'Connected Apps':
          return hasTrainerPermission('connectedAppsRole', 1);
        case 'Trainers':
          return hasTrainerPermission('subtrainerRole', 1);
        case 'Assessments':
          return hasTrainerPermission('assessmentsRole', 3);
        case 'Resources':
          return hasTrainerPermission('resourcesRole', 3);
        case 'Videos':
          return hasTrainerPermission('videosRole', 3);
        case 'Reports':
          return (
            (trainer &&
              trainer.reportsRole &&
              trainer.reportsRole.length > 0) ||
            isHQPlatform(session)
          );
        case 'Time Card':
          return hasTrainerPermission('clockRole', 2);
        case 'Emails':
          return isAdminTrainer() || isAdmin(sessionUser);
        case 'Stripe':
          return isAdminTrainer();
        case 'Customize Platform':
          return isAdminTrainer();
        case 'Business Dashboard':
          return isAdminTrainer();
        case 'Products':
          return isAdminTrainer() || hasTrainerPermission('inventoryRole', 2);
        case 'Lifecycle':
          return isAdminTrainer();
        case 'Tags':
          return isAdminTrainer();
        case 'Email Templates':
          return isAdmin(sessionUser);
        case 'Measurement Reports':
          return hasTrainerPermission('clientsRole', 0);
        case 'Point of Sale':
          return hasTrainerPermission('posRole', 1);
        default:
          return true;
      }
    },
    [hasTrainerPermission, sessionUser, trainer, isAdminTrainer, session]
  );

  const cannotEditExercise = useCallback(
    (exercise: ExerciseDetail) => {
      return hasNotEditPermission<ExerciseDetail>(
        'exercisesRole',
        'Exercise',
        exercise
      );
    },
    [hasNotEditPermission]
  );

  const canImportExercise = hasTrainerPermission(
    'exercisesRole',
    TrainerPermissions.ManageAll
  );

  const canOnlyManageLocationClient =
    isSubTrainer(sessionUser) &&
    trainer?.clientsRole === TrainerPermissions.ManageLocation;

  const canManageClientsForTrainer =
    isHeadTrainer(sessionUser) ||
    (isSubTrainer(sessionUser) &&
      hasTrainerPermission('clientsRole', TrainerPermissions.ManageAll));

  const canViewAssessment =
    isHeadTrainer(sessionUser) ||
    (isSubTrainer(sessionUser) &&
      hasTrainerPermission('assessmentsRole', TrainerPermissions.ManageOwn));

  const canNotEditYourAssessment =
    isSubTrainer(sessionUser) &&
  trainer?.assessmentsRole >= TrainerPermissions.ManageNone;

  const canCreateGroup =
    isHeadTrainer(sessionUser) ||
    (isSubTrainer(sessionUser) &&
      hasTrainerPermission('groupsRole', TrainerPermissions.ManageLocation));

  const canViewGroup =
    isHeadTrainer(sessionUser) ||
    (isSubTrainer(sessionUser) &&
      hasTrainerPermission('groupsRole', TrainerPermissions.ManageOwn));

  const canEditGroup = canViewGroup;

  const canAssignWorkoutPlan =
    isHeadTrainer(sessionUser) ||
    (isSubTrainer(sessionUser) &&
      hasTrainerPermission('workoutPlansRole', TrainerPermissions.ManageOwn));

  const canViewResource =
    isHeadTrainer(sessionUser) ||
    (isSubTrainer(sessionUser) &&
      hasTrainerPermission('resourcesRole', TrainerPermissions.ManageOwn));

  const canViewVideo =
    isHeadTrainer(sessionUser) ||
    (isSubTrainer(sessionUser) &&
      hasTrainerPermission('videosRole', TrainerPermissions.ManageOwn));

  const canNotEditVideo = useCallback(
    (subTrainerId: Identifier) => {
      return (
        isSubTrainer(sessionUser) &&
        trainer['videosRole'] >= TrainerPermissions.ManageNone &&
        subTrainerId != sessionUser.id
      );
    },
    [sessionUser, trainer]
  );

  // Discussion management (pin/unpin, disable/enable comments, delete)
  // A user can manage discussions if they are a Head Trainer, a platform Admin, or a listed admin of the group
  const canMangeGroupDiscussion = useCallback(
    (group: Group) => {
      if (!group) return false;
      if (isHeadTrainer(sessionUser) || isAdmin(sessionUser)) return true;
      return isGroupAdmin({ user: sessionUser, group });
    },
    [sessionUser]
  );

  // Scheduling is restricted to admins/head trainers or group admins
  const canScheduleGroupDiscussion = canMangeGroupDiscussion;

  const canPostGroupDiscussion = useCallback(
    (group: Group, memberships?: GroupMembership[] | null) => {
      if (!group) return false;
      // Platform-level admins/head trainers can always post
      if (isHeadTrainer(sessionUser) || isAdmin(sessionUser)) return true;

      const userIsGroupAdmin = isGroupAdmin({ user: sessionUser, group });

      // If admin-only discussion is enabled, only group admins can post
      if (group.adminOnlyDiscussion) {
        return userIsGroupAdmin;
      }

      // Otherwise admins and members can post
      return userIsGroupAdmin || isGroupMember({ group, memberships });
    },
    [sessionUser]
  );

  // EXE-8090: can upload media to group discussions
  const canUploadGroupMedia = useCallback(
    (group: Group, memberships?: GroupMembership[] | null) => {
      if (!group) return false;
      // Admins/head trainers bypass flag
      if (isHeadTrainer(sessionUser) || isAdmin(sessionUser)) return true;
      const userIsGroupAdmin = isGroupAdmin({ user: sessionUser, group });
      if (userIsGroupAdmin) return true;
      // If group prevents member media, only admins can upload
      if (group.preventMemberMediaUpload === true) return false;
      // Otherwise, members can upload
      return isGroupMember({ group, memberships });
    },
    [sessionUser]
  );

  const canViewRoutine = hasTrainerPermission(
    'routinesRole',
    TrainerPermissions.ManageLocation
  );

  const canEditRoutine = hasTrainerPermission(
    'routinesRole',
    TrainerPermissions.ManageOwn
  );

  const cannotEditRoutine = useCallback(
    (routine: Routine) => {
      return hasNotEditPermission<Routine>(
        'routinesRole',
        'PlanWorkout',
        routine
      );
    },
    [hasNotEditPermission]
  );

  const canViewWorkoutPlan = hasTrainerPermission(
    'workoutPlansRole',
    TrainerPermissions.ManageLocation
  );

  const cannotEditWorkoutPlan = useCallback(
    (plan: WorkoutPlan) => {
      return hasNotEditPermission<WorkoutPlan>(
        'workoutPlansRole',
        'WorkoutPlan',
        plan
      );
    },
    [hasNotEditPermission]
  );

  const canCancelVisit = hasTrainerPermission(
    'cancelVisitsRole',
    TrainerPermissions.ManageOwn
  );

  const canEarlyCancelVisit = hasTrainerPermission(
    'cancelVisitsRole',
    TrainerPermissions.ManageAll,
    true
  );

  const canManageStripe = useMemo(() => {
    return (
      isHeadTrainer(sessionUser) ||
      (isSubTrainer(sessionUser) &&
        (hasTrainerPermission(
          'connectedAppsRole',
          TrainerPermissions.ManageAll,
          true
        ) ||
          hasTrainerPermission(
            'connectedAppsRole',
            TrainerPermissions.ManageOwn,
            true
          )))
    );
  }, [sessionUser, hasTrainerPermission]);

  const canEditOthersSchedules = hasTrainerPermission(
    'schedulesRole',
    TrainerPermissions.ManageAll
  );

  const canEditSchedules = useMemo(() => {
    const editOwn = trainer?.schedulesRole <= 1 && sessionUser.id == trainer.id;

    return (
      hasTrainerPermission('schedulesRole', TrainerPermissions.ManageAll) ||
      editOwn
    );
  }, [
    hasTrainerPermission,
    sessionUser.id,
    trainer?.id,
    trainer?.schedulesRole,
  ]);

  const canManageServices = useMemo(() => {
    return (
      hasTrainerPermission('schedulingRole', TrainerPermissions.ManageOwn) ||
      hasTrainerPermission('schedulingRole', 5, true)
    );
  }, [hasTrainerPermission]);

  const canConnectApps = hasTrainerPermission(
    'connectedAppsRole',
    TrainerPermissions.ManageOwn
  );

  const canViewClient = useMemo(() => {
    return (
      isHeadTrainer(sessionUser) ||
      (isSubTrainer(sessionUser) &&
        (hasTrainerPermission('clientsRole', TrainerPermissions.ManageOwn) ||
          hasTrainerPermission(
            'clientsRole',
            TrainerPermissions.ManageLocation,
            true
          )))
    );
  }, [sessionUser, hasTrainerPermission]);

  const canViewDashboardKpi = useCallback(
    (kpiId: string) => {
      // Head trainers can see everything
      if (isHeadTrainer(sessionUser)) return true;

      // For sub-trainers, check if the KPI is in the disabled list
      if (isSubTrainer(sessionUser) && trainer) {
        const disabledKpis = trainer.disabledDashboardKpis || [];
        return !disabledKpis.includes(kpiId);
      }

      // Default to showing for regular users
      return false;
    },
    [sessionUser, trainer]
  );

  const canViewDashboardChart = useCallback(
    (chartId: string) => {
      // Head trainers can see everything
      if (isHeadTrainer(sessionUser)) return true;

      // For sub-trainers, check if the chart is in the disabled list
      if (isSubTrainer(sessionUser) && trainer) {
        const disabledCharts = trainer.disabledDashboardCharts || [];
        return !disabledCharts.includes(chartId);
      }

      // Default to showing for regular users
      return false;
    },
    [sessionUser, trainer]
  );

  const canConnectStripe = useMemo(() => {
    return (
      isHeadTrainer(sessionUser) ||
      (isSubTrainer(sessionUser) &&
        hasTrainerPermission(
          'connectedAppsRole',
          TrainerPermissions.ManageAll,
          true
        ))
    );
  }, [sessionUser, hasTrainerPermission]);

  const canViewPos = isHeadTrainer(sessionUser) || isSubTrainer(sessionUser);

  // POS permissions based on posRole
  // 0 - canManageAll, 1 - canManageOnlyForMyLocation, 2 - cannotManage
  const canManagePos = useMemo(() => {
    if (isHeadTrainer(sessionUser)) return true;
    if (!isSubTrainer(sessionUser)) return false;
    return trainer && trainer.posRole !== undefined && trainer.posRole <= 1;
  }, [sessionUser, trainer]);

  // Inventory permissions based on inventoryRole
  // 0 - canManageAll, 1 - canViewInventoryForMyLocation, 2 - canAdjustInventoryForMyLocation,
  // 3 - canViewAndAdjustInventoryForAllLocations, 4 - cannotViewInventory
  const canViewInventory = useMemo(() => {
    if (isHeadTrainer(sessionUser)) return true;
    if (!isSubTrainer(sessionUser)) return false;
    return (
      trainer &&
      trainer.inventoryRole !== undefined &&
      trainer.inventoryRole <= 2
    );
  }, [sessionUser, trainer]);

  const canViewOtherLocationInventory = useMemo(() => {
    if (isHeadTrainer(sessionUser)) return true;
    if (!isSubTrainer(sessionUser)) return false;
    return trainer && trainer.inventoryRole === 0;
  }, [sessionUser, trainer]);

  const canAdjustInventory = useMemo(() => {
    if (isHeadTrainer(sessionUser)) return true;
    if (!isSubTrainer(sessionUser)) return false;
    return (
      trainer && (trainer.inventoryRole === 0 || trainer.inventoryRole === 2)
    );
  }, [sessionUser, trainer]);

  const canAdjustOtherLocationInventory = useMemo(() => {
    if (isHeadTrainer(sessionUser)) return true;
    if (!isSubTrainer(sessionUser)) return false;
    return trainer && trainer.inventoryRole === 0;
  }, [sessionUser, trainer]);

  const canTransferInventory = useMemo(() => {
    // Transfer requires ability to adjust inventory across multiple locations
    if (isHeadTrainer(sessionUser)) return true;
    if (!isSubTrainer(sessionUser)) return false;
    return trainer && trainer.inventoryRole === 0;
  }, [sessionUser, trainer]);

  const canViewInventoryHistory = useMemo(() => {
    // Same as viewing inventory
    return canViewInventory;
  }, [canViewInventory]);

  const canExportInventoryHistory = useMemo(() => {
    if (isHeadTrainer(sessionUser)) return true;
    if (!isSubTrainer(sessionUser)) return false;
    return (
      trainer &&
      trainer.inventoryRole !== undefined &&
      trainer.inventoryRole <= 2
    );
  }, [sessionUser, trainer]);

  const canAddRevenueShare = useMemo(() => {
    return (
      hasTrainerPermission('connectedAppsRole', TrainerPermissions.ManageOwn) ||
      isAdmin(sessionUser)
    );
  }, [hasTrainerPermission, sessionUser]);

  const currentUserCanManageAllMessages = useMemo(() => {
    return hasTrainerPermission('messagesRole', TrainerPermissions.ManageAll);
  }, [hasTrainerPermission]);

  const currentUserCanManageLocationMessages = useMemo(() => {
    return hasTrainerPermission(
      'messagesRole',
      TrainerPermissions.ManageLocation,
      true
    );
  }, [hasTrainerPermission]);

  const canViewMacroCalculatorSettings = useMemo(() => {
    return isHeadTrainer(sessionUser) || isSubTrainer(sessionUser);
  }, [sessionUser]);

  const { canViewHQFranchiseFees, canEditHQFranchiseFees } =
    useHQPermissionCheck();

  const canViewScheduling = useMemo(() => {
    return (
      hasTrainerPermission('schedulingRole', TrainerPermissions.ManageNone) ||
      hasTrainerPermission('schedulingRole', 4, true) ||
      hasTrainerPermission('schedulingRole', 5, true)
    );
  }, [hasTrainerPermission]);

  /**
   * @see /v2/app/abilities/scheduling.js/canFrontDeskStaff
   */
  const canAccessFrontDeskAsStaff = useMemo(() => {
    return (
      isSubTrainer(sessionUser) &&
      hasPermission('messagesRole', TrainerPermissions.ManageOwn) &&
      hasPermission('assessmentsRole', TrainerPermissions.ManageNone) &&
      hasPermission('resourcesRole', TrainerPermissions.ManageNone) &&
      hasPermission('videosRole', TrainerPermissions.ManageNone) &&
      hasPermission('routinesRole', TrainerPermissions.ManageLocation) &&
      hasPermission('connectedAppsRole', TrainerPermissions.ManageNone) &&
      hasPermission('workoutPlansRole', TrainerPermissions.ManageLocation) &&
      hasPermission('exercisesRole', TrainerPermissions.ManageLocation) &&
      hasPermission('schedulesRole', TrainerPermissions.ManageOwn) &&
      hasPermission('clientsRole', TrainerPermissions.ManageAll) &&
      hasPermission('groupsRole', TrainerPermissions.ManageNone) &&
      hasPermission('schedulingRole', TrainerPermissions.ManageNone)
    );
  }, [hasPermission, sessionUser]);

  /**
   * @see /v2/app/abilities/scheduling.js/canFrontDeskAdmin
   */
  const canAccessFrontDeskAsAdmin = useMemo(() => {
    return (
      isSubTrainer(sessionUser) &&
      hasPermission('messagesRole', TrainerPermissions.ManageOwn) &&
      hasPermission('assessmentsRole', TrainerPermissions.ManageNone) &&
      hasPermission('resourcesRole', TrainerPermissions.ManageNone) &&
      hasPermission('videosRole', TrainerPermissions.ManageNone) &&
      hasPermission('routinesRole', TrainerPermissions.ManageLocation) &&
      hasPermission('connectedAppsRole', TrainerPermissions.ManageNone) &&
      hasPermission('workoutPlansRole', TrainerPermissions.ManageLocation) &&
      hasPermission('exercisesRole', TrainerPermissions.ManageLocation) &&
      hasPermission('schedulesRole', TrainerPermissions.ManageAll) &&
      hasPermission('clientsRole', TrainerPermissions.ManageAll) &&
      hasPermission('groupsRole', TrainerPermissions.ManageNone) &&
      hasPermission('schedulingRole', TrainerPermissions.ManageAll)
    );
  }, [hasPermission, sessionUser]);

  const canManageLocations =
    hasTrainerPermission('schedulingRole', TrainerPermissions.ManageOwn) ||
    hasTrainerPermission('schedulingRole', 5, true);

  const canViewReport = useCallback(
    (reportType: string) => {
      return (
        isHeadTrainer(sessionUser) ||
        (isSubTrainer(sessionUser) &&
          trainer &&
          trainer.reportsRole.includes(reportType))
      );
    },
    [sessionUser, trainer]
  );

  return useMemo(() => {
    return {
      trainer,
      sessionUser,
      permissionsLoading: trainerLoading,
      permissionsError: trainerError,
      canViewTimeTracking,
      canViewTasks,
      canViewOtherTimeClock,
      canViewOthersScheduling,
      canEditLogEntries,
      canClockIn,
      canViewStaffUserMenu,
      canViewApiKeys,
      canViewWebhooks,
      canExportTimeClockReport,
      canViewAppointmentMembers,
      canEditAppointmentMembers,
      canCheckInAppointment,
      canGlobalCheckIn,
      canEditNoteFields,
      canViewTrainerTab,
      canViewScheduleSubtab,
      canViewTrainerSidebarTabs,
      canViewClientTab,
      canViewClientManagementTab,
      canViewBillingInfo,
      canOnlyManageLocationClient,
      canManageClientsForTrainer,
      canViewAssessment,
      canNotEditYourAssessment,
      canViewGroup,
      canAssignWorkoutPlan,
      canViewResource,
      canViewVideo,
      canNotEditVideo,
      canCreateGroup,
      canEditGroup,
      canMangeGroupDiscussion,
      canScheduleGroupDiscussion,
      canPostGroupDiscussion,
      canUploadGroupMedia,
      cannotEditExercise,
      canImportExercise,
      canViewMessages,
      canViewRoutine,
      canEditRoutine,
      cannotEditRoutine,
      canViewWorkoutPlan,
      cannotEditWorkoutPlan,
      canCancelVisit,
      canEarlyCancelVisit,
      canViewDashboardObject,
      canManageAppNavigation,
      canManagePages,
      canManageAll,
      canManageStripe,
      canManageTrainerDashboardNavigation,
      canManageTrainerAccountNavigation,
      canManageClientManagementNavigation,
      canManageClientDashboardNavigation,
      canManageClientAccountNavigation,
      canEditOthersSchedules,
      canEditSchedules,
      canManageServices,
      canConnectStripe,
      canConnectApps,
      canAddRevenueShare,
      canViewClient,
      canViewInventory,
      canViewOtherLocationInventory,
      canAdjustInventory,
      canAdjustOtherLocationInventory,
      canTransferInventory,
      canViewInventoryHistory,
      canExportInventoryHistory,
      canViewDashboardKpi,
      canViewDashboardChart,
      canViewPos,
      canManagePos,
      currentUserCanManageAllMessages,
      currentUserCanManageLocationMessages,
      canViewMacroCalculatorSettings,
      canViewHQFranchiseFees,
      canEditHQFranchiseFees,
      canAccessFrontDeskAsStaff,
      canAccessFrontDeskAsAdmin,
      canManageLocations,
      canViewScheduling,
      canViewReport,
    };
  }, [
    canCheckInAppointment,
    canGlobalCheckIn,
    canClockIn,
    canEditAppointmentMembers,
    canEditLogEntries,
    canEditNoteFields,
    canExportTimeClockReport,
    canViewApiKeys,
    canViewAppointmentMembers,
    canViewTrainerTab,
    canViewScheduleSubtab,
    canViewClientManagementTab,
    canViewOtherTimeClock,
    canViewOthersScheduling,
    canViewStaffUserMenu,
    canViewTasks,
    canViewTimeTracking,
    canViewWebhooks,
    trainer,
    trainerError,
    trainerLoading,
    sessionUser,
    canViewTrainerSidebarTabs,
    canViewClientTab,
    canViewBillingInfo,
    canOnlyManageLocationClient,
    canManageClientsForTrainer,
    canViewAssessment,
    canNotEditYourAssessment,
    canViewGroup,
    canEditGroup,
    canMangeGroupDiscussion,
    canScheduleGroupDiscussion,
    canPostGroupDiscussion,
    canUploadGroupMedia,
    canAssignWorkoutPlan,
    canViewResource,
    canViewVideo,
    canNotEditVideo,
    canCreateGroup,
    cannotEditExercise,
    canImportExercise,
    canViewMessages,
    canViewRoutine,
    canEditRoutine,
    cannotEditRoutine,
    canViewWorkoutPlan,
    cannotEditWorkoutPlan,
    canCancelVisit,
    canEarlyCancelVisit,
    canViewDashboardObject,
    canManageAppNavigation,
    canManagePages,
    canManageAll,
    canManageStripe,
    canManageTrainerDashboardNavigation,
    canManageTrainerAccountNavigation,
    canManageClientManagementNavigation,
    canManageClientDashboardNavigation,
    canManageClientAccountNavigation,
    canEditOthersSchedules,
    canEditSchedules,
    canManageServices,
    canConnectStripe,
    canConnectApps,
    canAddRevenueShare,
    canViewClient,
    canViewInventory,
    canViewOtherLocationInventory,
    canAdjustInventory,
    canAdjustOtherLocationInventory,
    canTransferInventory,
    canViewInventoryHistory,
    canExportInventoryHistory,
    canViewDashboardKpi,
    canViewDashboardChart,
    canViewPos,
    canManagePos,
    currentUserCanManageAllMessages,
    currentUserCanManageLocationMessages,
    canViewMacroCalculatorSettings,
    canViewHQFranchiseFees,
    canEditHQFranchiseFees,
    canAccessFrontDeskAsStaff,
    canAccessFrontDeskAsAdmin,
    canManageLocations,
    canViewScheduling,
    canViewReport,
  ]);
};

export function hasFeatureFlag(session: Session, flag: FeatureFlag): boolean {
  return !!session.featureFlags?.includes(flag);
}
